/**
 * TypeScript types for Team Swimmer Feedback Data
 *
 * This defines the complete structure for the new team feedback format
 * based on team_swimmer_data.json
 */

import type { StrengthTextLevel, WeaknessTextLevel } from '../../../pure-js/enums/FeedbackLevel'
import type { TeamPsychologicalSafetyKey } from '../../../pure-js/types/Session'

// ============================================================================
// Visualization Types
// ============================================================================

/**
 * Enum for Team Swimmer visualization types
 * These match the "type" field in the JSON data
 */
export enum TeamSwimmerVisualizationType {
  QUOTE = 'quote',
  STAR_CHART = 'star_chart',
  TEAM_HEALTH_HEART = 'team_health_heart',
  PART_OF_WHOLE = 'part_of_whole',
  COMPARISON_BAR_CHART = 'comparison_bar_chart',
  STACKED_LINE_CHART_CENTERED = 'stacked_line_chart_centered',
  LINE_CHART = 'line_chart',
  NUMBER = 'number',
  PERCENT = 'percent',
  EMOTION = 'emotion'
}

export interface QuoteVisualization {
  type: 'quote'
  text: string
  speaker: string
}

export interface StarChartVisualization {
  type: 'star_chart'
  values: number[]
}

export interface TeamHealthHeartVisualization {
  type: 'team_health_heart'
  value: number
}

export interface PartOfWholeVisualization {
  type: 'part_of_whole'
  value: number
}

export interface ComparisonBarChartVisualization {
  type: 'comparison_bar_chart'
  values: Array<{
    label: string
    value: number
  }>
}

export interface StackedLineChartCenteredVisualization {
  type: 'stacked_line_chart_centered'
}

export interface LineChartVisualization {
  type: 'line_chart'
}

export interface NumberVisualization {
  type: 'number'
  value: number
  rawValue?: number
}

export interface PercentVisualization {
  type: 'percent'
  value: number
  rawValue?: number
}

export interface EmotionVisualization {
  type: 'emotion'
}

export type TeamSwimmerVisualization =
  | QuoteVisualization
  | StarChartVisualization
  | TeamHealthHeartVisualization
  | PartOfWholeVisualization
  | ComparisonBarChartVisualization
  | StackedLineChartCenteredVisualization
  | LineChartVisualization
  | NumberVisualization
  | PercentVisualization
  | EmotionVisualization

// ============================================================================
// Team Session Profile
// ============================================================================

export interface ProfileStrengthItem {
  variable: string
  normalizedScore: number
  value?: number
}

export interface TeamProfileTab {
  topStrength: ProfileStrengthItem
  secondStrength: ProfileStrengthItem
}

// ============================================================================
// Planning Executing Evaluating (Array of 3 tabs)
// ============================================================================

export interface PlanTab {
  teamMadePlan: boolean
  howMadePlan: string
  visualization: QuoteVisualization
}

export interface FollowTab {
  teamFollowedPlan: boolean
  howFollowedPlan: string
  visualization: QuoteVisualization
}

export interface EvaluateTab {
  teamEvaluatedPlan: boolean
  visualization: QuoteVisualization
}

// ============================================================================
// Team Psychological Safety (Array of 3 tabs)
// ============================================================================

export type PsychologicalSafetyQuestions = Record<TeamPsychologicalSafetyKey, number>

export interface PsychologicalSafetyTab {
  teamHealth: number
  questions: PsychologicalSafetyQuestions
  visualization: StarChartVisualization
}

export interface TeamHealthTab {
  teamHealth: number
  visualization: TeamHealthHeartVisualization
}

export interface ConstructiveDisagreementTab {
  disagreeRatio: number
  visualization: PartOfWholeVisualization
}

// ============================================================================
// Task Performance (Array of 3 tabs)
// ============================================================================

export interface PerformanceTab {
  uridiumPer15Min: {
    current: number
    previous: number
  }
  visualization: ComparisonBarChartVisualization
}

export interface HypothermiaTab {
  teamTimeParalyzed: {
    current: number
    previous: number
  }
  visualization: ComparisonBarChartVisualization
}

export interface DroneTab {
  hasUnlockedDrone: number
  visualization: QuoteVisualization
}

// ============================================================================
// Communication Climate (Array of 3 tabs)
// ============================================================================

export interface PlayerCommunicationData {
  playerId: string
  displayName: string
  talkedTotal: number
}

export interface LoopCommunicationData {
  loopIndex: number
  players: PlayerCommunicationData[]
}

export interface CommunicationBalanceTab {
  visualization: StackedLineChartCenteredVisualization
  loops: LoopCommunicationData[]
}

export interface CommunicatedEmotionsTab {
  teamCommunicatedEmotionClass: string
  visualization: EmotionVisualization
}

export interface InclusionAndInvitationsTab {
  teamTotalInvitations: number
  visualization: NumberVisualization
}

// ============================================================================
// Improvement Areas & Strengths (Arrays of items)
// ============================================================================

export interface ImprovementAreaItem {
  variable: string
  normalizedScore: number
  recommendationDirection: 'increase' | 'decrease'
  visualization: NumberVisualization
  rawValue?: number
  /** Which text describes it. */
  textLevel?: WeaknessTextLevel
}

export interface StrengthItem {
  variable: string
  normalizedScore: number
  recommendationDirection: 'increase' | 'decrease'
  visualization: NumberVisualization
  rawValue?: number
  /** Which text describes it. */
  textLevel?: StrengthTextLevel
}

// ============================================================================
// Root Interface (ALL ARRAYS!)
// ============================================================================

export interface TeamSwimmerFeedbackResponse {
  teamSessionProfile: TeamProfileTab[] // Array with 1 element
  planningExecutingEvaluating: (PlanTab | FollowTab | EvaluateTab)[] // Array of 3 tabs
  teamPsychologicalSafety: (PsychologicalSafetyTab | TeamHealthTab | ConstructiveDisagreementTab)[] // Array of 3 tabs
  taskPerformance: (PerformanceTab | HypothermiaTab | DroneTab)[] // Array of 3 tabs
  communicationClimate: (CommunicationBalanceTab | CommunicatedEmotionsTab | InclusionAndInvitationsTab)[] // Array of 3 tabs
  improvementAreas: ImprovementAreaItem[] // Array of improvement items
  strengths: StrengthItem[] // Array of strength items
}
